import { useToast } from "@/components/ui";
import { useUserContext } from "@/context/AuthContext";
import { getExistingUserForGoogle, saveUserToDB } from "@/lib/appwrite/api";
import { account, avatars } from "@/lib/appwrite/config";
import { isUserBanned } from "@/lib/appwrite/userViolationAPI";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

export const AuthCallback = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { setUser, setIsAuthenticated } = useUserContext();
  const isProcessing = useRef(false); // Use a ref to track processing state

  useEffect(() => {
    const handleAuthCallback = async () => {
      // If already processing, return early
      if (isProcessing.current) return;
      isProcessing.current = true;

      try {
        const accountUser = await account.get();
        if (!accountUser) throw new Error("No accountUser data received");
        const userIsBanned = await isUserBanned(accountUser.$id);
        if (userIsBanned) {
          toast({
            title: "Account Suspended",
            description: "Your account is under ban due to repeated violations of our community guidelines. Contact contact@busiconn.com for assistance.",
            variant: "destructive"
          });
          navigate("/sign-in?banned=true");
          return;
        }
        
        const existingUser = await getExistingUserForGoogle(accountUser);
        if (existingUser.documents.length > 0) {
          toast({
            title: "Welcome back!",
            description: "Successfully signed in to your account.",
          });
          setIsAuthenticated(true);
          setUser(existingUser.documents[0] as any);
          navigate('/');
          return;
        }

        const avatarUrl = avatars.getInitials(accountUser.name);

        const newUser = await saveUserToDB({
          accountId: accountUser.$id,
          name: accountUser.name,
          emailOrPhone: accountUser.email,
          username: accountUser.name,
          imageUrl: avatarUrl,
        });

        if (!newUser) throw new Error("Failed to save user to database");

        toast({
          title: "Sign up successful!",
          description: "Welcome to our platform!",
        });
        setIsAuthenticated(true);
        setUser(newUser as any);
        navigate('/');

      } catch (error) {
        console.error("Auth callback error:", error);
        toast({
          title: "Authentication failed",
          description: "Please try signing up again",
          variant: "destructive",
        });
        navigate('/sign-up');
      } finally {
        isProcessing.current = false; // Reset flag after processing
      }
    };

    handleAuthCallback();
  }, [toast, navigate]); // Ensure dependencies are correct

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <h2 className="text-xl font-semibold mb-2">Processing your signup/signin...</h2>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
      </div>
    </div>
  );
};
